<template>
    <div v-if="layout === 'week'" class="ds-week-employee" :class="{ 'ds-week-employee-warned': warned || hoursLimitViolation }">
        <Tooltip :text="tooltipText" placement="bottom" :delay="warned || hoursLimitViolation ? 0 : 1500" :wrap="Boolean(hoursLimitViolation || (schedule.is_outsource && schedule.supplier_name))">
            <template #trigger>
                <div class="ds-week-employee-person">
                    <Avatar :first-name="firstName" :last-name="lastName" :url="avatar" size="lg" width="40" height="40" class="ds-week-employee-avatar" />
                    <div class="ds-week-employee-info">
                        <div class="ds-week-employee-name">{{ lastName }}</div>
                        <div class="ds-week-employee-name">{{ firstName }} {{ middleName }}</div>
                        <div class="ds-week-employee-position">{{ position }}</div>
                    </div>
                </div>
            </template>
        </Tooltip>
        <div class="ds-week-metrics-cell">
            <!-- Табель (RD-139) подставляет месячные итоги вместо выбранных показателей. -->
            <slot name="metrics">
                <template v-if="store.isEmployeeStatsVisible">
                    <span class="ds-week-metric-a">{{ formatStat(userStat) }}</span>
                    <span class="ds-week-metric-b">{{ formatStat(userStat2) }}</span>
                </template>
            </slot>
        </div>
    </div>
    <div v-else class="ds-schedule-employee-content relative h-full">
        <Avatar :first-name="firstName" :last-name="lastName" :url="avatar" font-size="xs" class="ds-schedule-employee-avatar ml-2 mr-2" />
        <Tooltip :text="tooltipText" placement="bottom" :delay="warned || hoursLimitViolation ? 0 : 1500" :wrap="Boolean(hoursLimitViolation || (schedule.is_outsource && schedule.supplier_name))">
            <template #trigger>
                <div class="ds-schedule-employee-info !pt-0 text-gray flex-1 min-w-0">
                    <div class="text-[13px] leading-[13px] font-normal text-ellipsis overflow-hidden max-w-40">
                        <div class="truncate">{{ lastName }}</div>
                        <div class="truncate">{{ firstName }} {{ middleName }}</div>
                    </div>
                    <div class="ds-schedule-employee-position mt-0.5">
                        <p>{{ position }}</p>
                    </div>
                </div>
            </template>
        </Tooltip>
        
        <div v-if="store.isEmployeeStatsVisible" class="shrink-0 pt-1 w-11 text-xs pl-1 pr-3">
            <div class="text-darkred text-right" >{{ userStat }}</div>
            <div class="text-darkblue text-right" >{{ userStat2 }}</div>
        </div>
        <div :class="{'bg-orange': warned || hoursLimitViolation}" class="absolute top-0 left-0 w-[4px] h-full z-10"></div>
    </div>
</template>

<script setup lang="ts">
import { HoursLimitEmployment, Schedule } from '@/types'
import Avatar from '../Avatar.vue'
import { computed, toRefs } from 'vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import dayjs from 'dayjs'
import duration from "dayjs/plugin/duration"
import Tooltip from '@/components/Tooltip.vue'

dayjs.extend(duration)

interface Props {
    schedule: Schedule,
    position?: string,
    warned?: boolean,
    hoursLimitViolation?: HoursLimitEmployment
    layout?: 'month' | 'week'
}

const store = useScheduleStore()

const props = defineProps<Props>()
const { schedule } = toRefs(props)

const firstName = computed(() => schedule.value.user.first_name)
const lastName = computed(() => schedule.value.user.last_name)
const middleName = computed(() => schedule.value.user.middle_name)
const avatar = computed(() => schedule.value.user.avatar)

const userStat = computed(() => calc(store.userStat?.value))
const userStat2 = computed(() => calc(store.userStat2?.value))
const tooltipText = computed(() => {
    const warnings: string[] = []

    if (props.warned) warnings.push('Нарушение правила 42 часов')
    if (props.hoursLimitViolation) {
        const { planned_hours, hours_norm, delta } = props.hoursLimitViolation
        warnings.push(`Превышена норма плановых часов\nЗапланировано: ${formatHours(planned_hours)} ч.\nНорма: ${formatHours(hours_norm)} ч.\nПревышение: ${formatHours(delta)} ч.`)
    }
    const warningText = warnings.join('\n')
    const outsourceText = schedule.value.is_outsource && schedule.value.supplier_name
        ? `Аутсорс-компания: ${schedule.value.supplier_name}`
        : undefined

    return [warningText, outsourceText].filter(Boolean).join('. ') || undefined
})

function formatHours(value: string | number) {
    const hours = Number(value)

    return Number.isFinite(hours) ? String(hours) : String(value)
}

// Недельный вид показывает показатели целыми числами (RD-137); месячный — как раньше.
function formatStat(value: unknown) {
    if (value === undefined || value === null || value === '') return ''
    const numeric = Number(value)
    return Number.isFinite(numeric) ? Math.round(numeric) : value
}

function calc(stat:any) {
    return stat ? store.stats?.[stat]?.values.find((item:any) => item.employment_id === schedule.value.id)?.value : undefined
}

</script>
